/* eslint-disable max-lines */

import type { ConnectionPositionPair } from '@angular/cdk/overlay';
import { NgTemplateOutlet } from '@angular/common';
import type { SimpleChanges, TemplateRef } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  Optional,
  Output,
  Self,
  signal,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { NgControl } from '@angular/forms';

import { VirtualScrollerModule } from '@iharbeck/ngx-virtual-scroller';
import { fromEvent as observableFromEvent } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { NgTemplateAssertType } from '@core/common-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { generateUuid, includesWithTrim, WithDefaultValue } from '@core/utils-legacy';

import { SelectDropdownComponent } from './select-dropdown/select-dropdown.component';
import { BadgeComponent } from '../../common/badge/badge.component';
import { ButtonComponent } from '../../common/button/components/button/button.component';
import { IconButtonComponent } from '../../common/button/components/icon-button/icon-button.component';
import { ButtonDropdownIndicatorDirective } from '../../common/button/directives/button-dropdown/button-dropdown-indicator.directive';
import { DropdownHeaderComponent } from '../../common/dropdown/dropdown-header/dropdown-header.component';
import { DropdownItemComponent } from '../../common/dropdown/dropdown-item/dropdown-item.component';
import { NestedTemplateTriggerDirective } from '../../common/dropdown/dropdown-nested-templates/nested-template-trigger.directive';
import type { DropdownComponent } from '../../common/dropdown/dropdown.component';
import type { IconToken } from '../../common/icon/directives/icon-color.directive';
import { IconComponent } from '../../common/icon/icon.component';
import type { IconName } from '../../common/icon/icons.type';
import { LabelComponent } from '../../common/label/label.component';
import { TagComponent } from '../../common/tag/tag.component';
import { RtTooltip } from '../../common/tooltip/tooltip.directive';
import { TruncateComponent } from '../../common/truncate/truncate.component';
import { KeyCode } from '../../core/keycode';
import {
  ConnectedOverlayDirective,
  OverlayOriginDirective,
} from '../../core/overlay/overlay-directives';
import { RtInput } from '../../utils/rt-input';
import { WindowRef } from '../../utils/window-ref';
import { FormFieldComponent } from '../form-field/form-field.component';
import { InputComponent } from '../input/input.component';

export type SelectOptionValue = string | number | null;
export type SelectOption<T = SelectOptionValue, IncludeEmpty extends boolean = false> = {
  label: string;
  value: T | (IncludeEmpty extends true ? '' : T) | null;
  disabled?: boolean;
  count?: number;
  additionalSearchKeys?: string[];
  icon?: IconName;
  iconColorToken?: IconToken;
  displayedSelectedLabel?: string;
  nestedOptions?: Array<SelectOption<T, IncludeEmpty>>;
  badgeText?: string;
  tooltipText?: string;
  clickHandler?: () => void;
};

export type SelectGroupedOptions<T = SelectOptionValue> = {
  id?: string;
  title: string | null;
  options: Array<SelectGroupOption<T>>;
};

export interface SelectGroupOption<T = SelectOptionValue> extends SelectOption<T> {
  uuid?: string;
}

export interface TriggerTemplateContext<O = SelectOption, I = any> {
  disabled: boolean;
  readOnly: boolean;
  placeholder: string;
  selectedOption: O;
  dropdownOpened: boolean;
  toggleDropdown: (open?: boolean) => void;
  $implicit: I;
}

export interface LabelTemplateContext<O = SelectOption> {
  option: O;
  removeValue: (optionValue: SelectOptionValue) => void;
  wrapLabelText: boolean;
}

export interface GroupsTemplateContext<Val> {
  $implicit: Array<SelectGroupedOptions<Val>>;
}

export interface SelectListTemplateContext<Val> {
  $implicit: Array<SelectOption<Val>>;
}

export interface SelectOptionContext<T> {
  option: SelectOption<T>;
  selectedOption: SelectOption | null;
}

export interface GroupHeaderTemplateContext {
  group: SelectGroupedOptions;
  first: boolean;
}

export type SelectGroupedSort = <T extends SelectGroupedOptions<any>>(a: T, b: T) => number;

const groupedOptionsDefaultSort: SelectGroupedSort = (a, b) =>
  a.title === b.title ? 0 : b.title ? -1 : 1;

// TODO blur event
@Component({
  selector: 'rt-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.disabled]': 'disabled',
    '[class.large-button]': 'largeButton',
  },
  imports: [
    ButtonComponent,
    ButtonDropdownIndicatorDirective,
    ConnectedOverlayDirective,
    DropdownHeaderComponent,
    DropdownItemComponent,
    FormFieldComponent,
    IconComponent,
    InputComponent,
    LabelComponent,
    NgTemplateOutlet,
    OverlayOriginDirective,
    SelectDropdownComponent,
    TagComponent,
    TranslatePipe,
    TruncateComponent,
    VirtualScrollerModule,
    NestedTemplateTriggerDirective,
    IconButtonComponent,
    BadgeComponent,
    NgTemplateAssertType,
    RtTooltip,
  ],
})
export class SelectComponent<Val extends any | any[]> extends RtInput<Val> {
  private readonly virtualScrollThreshold = 20;
  // Whether or not wrap text in default label
  public readonly defaultWrapLabelText: boolean = false;
  // Placeholder text
  public readonly defaultPlaceholder: string = this.i18n.translate('rtu.ui.select.placeholder');
  public readonly defaultHasSearch: boolean = true;
  public readonly defaultNotSelectable: boolean = false;
  public readonly defaultSearchCaseSensitive: boolean = false;
  public readonly defaultHasStickyFooter: boolean = false;
  public readonly defaultNoMaxHeight: boolean = false;
  public readonly defaultNoResultsMessage: string = this.i18n.translate('rtu.ui.select.no_results');
  public readonly defaultEmptyOptionLabel: string = this.i18n.translate('rtu.ui.select.none');
  public readonly defaultEmptyOptionValue: any = '';
  public readonly defaultMultiple: boolean = false;
  public readonly defaultCloseOnSelect: boolean = true;
  public readonly defaultHasDropdownSeparator: boolean = false;
  public readonly defaultDropdownIconHasMargin: boolean = true;

  // Deprecated
  @Input()
  public set items(items: Array<{ option: string; value: Val }>) {
    this.options = (items || []).map(item => ({ label: item.option, value: item.value }));
  }

  // Deprecated
  @Input()
  public set message(message: string) {
    this.noResultsMessage = message;
  }

  @Input()
  public searchStrategy: (item: SelectOption<Val, boolean>, searchQuery: string | null) => boolean;

  // Include empty option together with other options
  public get defaultIncludeEmptyOption(): boolean {
    return !this.multiple;
  }

  // Deprecated
  @Input()
  public set required(required: boolean) {
    this.includeEmptyOption = !required;
  }

  @Output()
  public get select(): any {
    // eslint-disable-next-line no-console
    (console.warn || console.log)(
      'SelectComponent.select is deprecated. Please use `valueChange` instead',
    );
    return this.valueChange;
  }

  public get hasGroups(): boolean {
    return this.groupedOptions && !!this.groupedOptions.length;
  }

  public get dropdownOpened(): boolean {
    return this.selectDropdownOverlay.open;
  }

  public get selectedItemUuid(): string | void {
    return (
      this.flatGroupsOptions &&
      this.flatGroupsOptions[this.selectedOptionIndex] &&
      this.flatGroupsOptions[this.selectedOptionIndex].uuid
    );
  }

  private get formattedOptions(): Array<SelectOption<Val>> {
    const options = [
      ...(this.includeEmptyOption ? [this.emptyOption] : []),
      ...(this.options || []),
    ];
    if (this.multiple) {
      return options.filter(option => !(<Val[]>this.value).find(v => option.value === v));
    } else {
      return options;
    }
  }

  @Input() public adjustDropdownWidthOnOpen: boolean = true;
  @Input() public options: Array<SelectOption<any>> = [];
  @Input() public groupedOptions: Array<SelectGroupedOptions<any>> = [];
  @Input() public groupedOptionsSort: SelectGroupedSort | false = groupedOptionsDefaultSort;
  @Input() public selectedOptionIconLeft?: IconName;

  @Input() public largeButton: boolean = false;
  @Input() public hasInputTrigger: boolean = false;

  // Prepends a label
  @Input() public label?: string;

  @Input() public tabIndex?: number;

  @WithDefaultValue('defaultPlaceholder')
  @Input()
  public placeholder?: string;

  // Enables search input
  @WithDefaultValue('defaultHasSearch')
  @Input()
  public hasSearch?: boolean;

  @WithDefaultValue('defaultNotSelectable')
  @Input()
  public notSelectable?: boolean;

  // Search case sensitivity
  @WithDefaultValue('defaultSearchCaseSensitive')
  @Input()
  public searchCaseSensitive?: boolean;

  @WithDefaultValue('defaultHasStickyFooter')
  @Input()
  public hasStickyFooter?: boolean;

  @WithDefaultValue('defaultNoMaxHeight')
  @Input()
  public noMaxHeight?: boolean;

  // Limit shown options to first x options
  @Input() public limit?: number;

  // Message to display when there are no results
  @WithDefaultValue('defaultNoResultsMessage')
  @Input()
  public noResultsMessage?: string;
  @WithDefaultValue('defaultIncludeEmptyOption')
  @Input()
  public includeEmptyOption?: boolean = false;

  @WithDefaultValue('defaultEmptyOptionLabel')
  @Input()
  public emptyOptionLabel: string = this.defaultEmptyOptionLabel;

  @WithDefaultValue('defaultEmptyOptionValue')
  @Input()
  public emptyOptionValue: any = this.defaultEmptyOptionValue;

  // A selection dropdown can allow multiple selections.
  @WithDefaultValue('defaultMultiple')
  @Input()
  public multiple?: boolean;

  // Whether or not the menu should close when a value is selected from the dropdown.
  @WithDefaultValue('defaultCloseOnSelect')
  @Input()
  public closeOnSelect?: boolean;

  @WithDefaultValue('defaultHasDropdownSeparator')
  @Input()
  public hasDropdownSeparator?: boolean;

  // Whether or not dropdown icon should have margin
  @WithDefaultValue('defaultDropdownIconHasMargin')
  @Input()
  public dropdownIconHasMargin?: boolean;

  @Input()
  public hideTextOverflow: boolean = false;

  // Custom element to trigger the menu to become visible. By default, shows select/input element (depending on the variant).
  @Input() public triggerTemplate?: TemplateRef<any>;
  @Input() public triggerTemplateContext?: Record<string, any> = {};

  @Input() public footerTemplate?: TemplateRef<any>;
  @Input() public footerTemplateContext?: Record<string, any> = {};

  @Input() public headerTemplate?: TemplateRef<any>;

  // Option dropdown element
  @Input() public optionTemplate?: TemplateRef<SelectOptionContext<any>>;

  // Selected option label element (used only when `multiple`)
  @Input() public labelTemplate?: TemplateRef<any>;

  @Input() public inputPrefixTemplate?: TemplateRef<{ selectedOption: SelectOption | null }>;

  @Input() public groupHeaderTemplate?: TemplateRef<any>;
  @Input() public virtualScrollEnabled: boolean = true;

  @Input()
  public searchPlaceholder: string;

  @WithDefaultValue('defaultWrapLabelText')
  @Input()
  public wrapLabelText?: boolean;
  public SelectOptionContext: SelectOptionContext<SelectOptionValue>;
  public TriggerTemplateContext: TriggerTemplateContext;
  public LabelTemplateContext: LabelTemplateContext;
  public GroupHeaderTemplateContext: GroupHeaderTemplateContext;
  public GroupsTemplateContext: GroupsTemplateContext<Val>;
  public ListTemplateContext: SelectListTemplateContext<Val>;

  public GroupsOrListTemplateContext: GroupsTemplateContext<Val> | SelectListTemplateContext<Val>;
  public searchQuery: string | null = null;

  public dropdownWidth?: number;
  public virtualScrollThresholdExceeded: boolean = false;

  // Used when multiple === false
  public selectedOption: SelectOption | null = null;
  // Used when multiple === true
  public selectedOptions: Array<SelectOption<Val>> = [];

  public selectedOptionIndex: number = -1;

  public selectedOptionLabel: string | null = null;

  public filteredOptions: Array<SelectOption<Val>> = [];
  public filteredGroupsOptions: SelectGroupedOptions[] = [];

  public flatGroupsOptions: SelectGroupOption[] = [];

  public scrollTop: number = 0;

  public displayClearButton = signal(false);

  // If not defined, the width is set automatically to the container's width
  @Input() public minDropdownWidth?: number | 'max-content';
  @Input() public maxDropdownWidth?: number;

  // If not set, the position is set automatically to the trigger's position
  @Input() public dropdownPositions?: ConnectionPositionPair[];

  @Input() public openDropdownOnInit: boolean = false;

  @Input() public hasItemPadding: boolean = false;

  @Input() public labelOverride: string | null = null;

  @Input() public boldPlaceholder?: boolean = false;

  @Input() public itemTextLeftPadding?: boolean = false;

  @Input() public itemTextNoPadding?: boolean = false;

  @Input() public iconEmptyLabel?: boolean = false;

  @Input() public optionWordBreak: boolean = false;
  @Input() public hasWhiteBackground: boolean = false;

  @Input() public hideSelectedOption: boolean = false;
  @Input() public isInvalid: boolean = false;
  @Input() public hasClearButton: boolean = false;

  public emptyOption = {
    label: this.emptyOptionLabel,
    value: this.emptyOptionValue,
  };

  @ViewChild(SelectDropdownComponent) public selectDropdown: SelectDropdownComponent;
  @ViewChild('selectDefaultTemplate') private selectDefaultTemplate: TemplateRef<any>;
  @ViewChild('optionDefaultTemplate', { static: true })
  private optionDefaultTemplate: TemplateRef<any>;
  @ViewChild('labelDefaultTemplate', { static: true })
  private labelDefaultTemplate: TemplateRef<any>;
  @ViewChild('footerDefaultTemplate', { static: true })
  private footerDefaultTemplate: TemplateRef<any>;
  @ViewChild('selectDropdownOverlay', { static: true })
  private selectDropdownOverlay: ConnectedOverlayDirective;
  @ViewChild('dropdownContainer', { static: true }) private dropdownContainer: any;
  @ViewChild('trigger') private trigger: any;
  @ViewChild('searchQueryInput') private searchQueryInput: any;
  @ViewChildren('filteredOptions') private filteredOptionNodes: any;

  private emptyGroupedOption: SelectGroupedOptions = {
    title: null,
    options: [this.emptyOption],
  };

  private destroyed: boolean = false;

  constructor(
    private windowRef: WindowRef,
    private i18n: I18nService,
    cdRef: ChangeDetectorRef,
    @Optional() @Self() ngControl?: NgControl,
  ) {
    super(cdRef, ngControl);
  }

  public onVirtualScrollUpdate(visibleItems: SelectOption[] | SelectGroupedOptions[]): void {
    this.cdRef.detectChanges();
  }

  public ngOnInit(): void {
    this.selectDropdownOverlay.attach.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
      this.updateDropdownPostionAsync();
      this.setTimeout(() => {
        this.cdRef.detectChanges();
      });
    });
    this.selectDropdownOverlay.detach.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
      this.setTimeout(() => {
        this.cdRef.detectChanges();
      });
    });
  }

  public ngOnDestroy(): void {
    super.ngOnDestroy();
    this.destroyed = true;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes.options && changes.options.previousValue !== changes.options.currentValue) ||
      (changes.groupedOptions &&
        changes.groupedOptions.previousValue !== changes.groupedOptions.currentValue) ||
      (changes.items && changes.items.previousValue !== changes.items.currentValue) ||
      (changes.value && changes.value.previousValue !== changes.value.currentValue)
    ) {
      this.calculateOptions();
      this.calculateSelectedOption();
      this.calculateSelectedOptionIndex();
      this.updateDropdownPostionAsync();
    }
    if (changes.emptyOptionLabel || changes.emptyOptionValue) {
      this.emptyOption = { label: this.emptyOptionLabel!, value: this.emptyOptionValue };
    }
  }

  public ngAfterViewInit(): void {
    if (!this.minDropdownWidth) {
      this.adjustDropdownWidth();
    }
    this.setTimeout(() => {
      this.adjustDropdownWidth();
      this.cdRef.detectChanges();
    }, 0);

    observableFromEvent(this.windowRef.nativeWindow, 'resize')
      .pipe(debounceTime(50), takeUntil(this.ngOnDestroy$))
      .subscribe(() => {
        this.adjustDropdownWidth();
      });

    if (this.openDropdownOnInit) {
      this.toggleDropdown(true);
    }
  }

  public get dropdownComponentRef(): DropdownComponent {
    return this.selectDropdown?.dropdownComponent;
  }

  public setFocus(): void {
    if (this.trigger && this.trigger.setFocus) {
      this.trigger.setFocus();
    }
  }

  public setSearchInputFocus(): void {
    if (this.searchQueryInput) {
      this.searchQueryInput.setFocus();
    }
  }

  public selectOption(option: SelectOption<Val>): void {
    if (this.notSelectable) {
      return;
    }

    const nextValue = this.multiple
      ? (<SelectOptionValue[] | any>this.value || [])
          .filter((v: any) => v !== option.value)
          .concat([option.value])
      : option && option.value;
    this.handleChange(nextValue);
    // @ts-ignore
    if (!this.cdRef['destroyed']) {
      if (this.closeOnSelect && this.dropdownOpened) {
        this.toggleDropdown(false);
      }
      this.cdRef.detectChanges();
    }

    if (!this.minDropdownWidth) {
      this.setTimeout(() => {
        this.adjustDropdownWidth();
        // @ts-ignore
        if (!this.cdRef['destroyed']) {
          this.cdRef.detectChanges();
        }
      }, 0);
    }
  }

  public changeSearchQuery(searchQuery: string | null): void {
    this.searchQuery = ['', null, undefined].indexOf(searchQuery) !== -1 ? null : searchQuery;
    this.calculateOptions();
    this.selectedOptionIndex = this.searchQuery !== null ? 0 : -1;
    this.updateDropdownPostionAsync();
    this.cdRef.detectChanges();
  }

  // TODO fix focus issues:
  // - TAB and SHIFT-TAB should always work
  //   (whether we tab to the input, click on it; whether the dropdown menu is still open or not)
  // - if we are focused on input without the dropdown menu opened;
  //   UP and DOWN shouldnt defocus the select

  public handleKeyDown(event: KeyboardEvent): void {
    if (this.disabled || this.readOnly) {
      return;
    }
    const key = event.which || event.keyCode;
    switch (key) {
      case KeyCode.Escape:
        if (this.dropdownOpened) {
          event.preventDefault();
          this.toggleDropdown(false);
          this.setFocus();
        }
        break;

      case KeyCode.Tab:
        if (this.dropdownOpened) {
          this.toggleDropdown(false);
        }
        this.setFocus();
        break;

      case KeyCode.Enter:
      case KeyCode.Space:
        event.preventDefault();
        if (this.dropdownOpened) {
          const selectedOption =
            this.selectedOptionIndex !== -1
              ? this.filteredOptions[this.selectedOptionIndex] ||
                this.flatGroupsOptions[this.selectedOptionIndex]
              : undefined;

          if (selectedOption) {
            this.selectOption(selectedOption);
          }
        } else {
          this.toggleDropdown(true);
        }
        break;

      case KeyCode.UpArrow:
        event.preventDefault();
        if (this.dropdownOpened) {
          this.selectUp();
          // this.setFocus();
          this.scrollToCurrentOption();
        } else {
          this.toggleDropdown(true);
        }
        break;

      case KeyCode.DownArrow:
        event.preventDefault();
        if (this.dropdownOpened) {
          this.selectDown();
          // this.setFocus();
          this.scrollToCurrentOption();
        } else {
          this.toggleDropdown(true);
        }
        break;

      default:
        return;
    }

    this.cdRef.detectChanges();
  }

  public handleSearchInputKeydown(event: KeyboardEvent): void {
    if (this.disabled || this.readOnly) {
      return;
    }

    const key = event.which || event.keyCode;
    switch (key) {
      case KeyCode.Escape:
        event.preventDefault();
        this.setFocus();
        this.toggleDropdown(false);
        this.setTimeout(() => this.setFocus());
        break;

      case KeyCode.Tab:
        this.setFocus();
        this.setTimeout(() => {
          this.toggleDropdown(false);
        });
        break;

      case KeyCode.Enter:
        event.preventDefault();
        const targetOption =
          this.filteredOptions[this.selectedOptionIndex] ||
          this.flatGroupsOptions[this.selectedOptionIndex];

        if (targetOption) {
          this.setFocus();
          this.selectOption(targetOption);
          this.setTimeout(() => this.setFocus());
        }
        break;

      case KeyCode.UpArrow:
        event.preventDefault();
        this.selectUp();
        this.setSearchInputFocus();
        this.scrollToCurrentOption();
        break;

      case KeyCode.DownArrow:
        event.preventDefault();
        this.selectDown();
        this.setSearchInputFocus();
        this.scrollToCurrentOption();
        break;

      default:
        return;
    }

    this.cdRef.detectChanges();
  }

  public removeValue = (optionValue: Val) => {
    const nextValue = (<any[]>(this.value || [])).filter(v => v !== optionValue);
    this.handleChange(<Val>nextValue);
    this.cdRef.detectChanges();
  };

  public toggleDropdown = (open: boolean = !this.dropdownOpened) => {
    if (this.adjustDropdownWidthOnOpen) {
      this.adjustDropdownWidth();
    }

    this.selectDropdownOverlay.open = open;
    if (open) {
      this.changeSearchQuery(null);
    }
    this.cdRef.detectChanges();
  };

  public trackByOption<T extends SelectOption<Val>>(
    index: number,
    option: T,
  ): Val | number | string | null {
    if (option) {
      return option.value;
    } else {
      return index;
    }
  }

  public trackByGroup(index: number, group: SelectGroupedOptions<Val>): string {
    return `${index}__${JSON.stringify(group.options)}`;
  }

  public scrollToCurrentOption(): void {
    if (
      this.filteredOptionNodes._results &&
      this.filteredOptionNodes._results[this.selectedOptionIndex] &&
      this.filteredOptionNodes._results[this.selectedOptionIndex].nativeElement
    ) {
      this.scrollTop =
        this.filteredOptionNodes._results[this.selectedOptionIndex].nativeElement.offsetTop;
    }
  }

  protected setValue(value: Val): void {
    this.value = value;
    this.calculateSelectedOption();
    this.calculateSelectedOptionIndex();
    this.updateDropdownPostionAsync();
  }

  private calculateSelectedOptionIndex(): void {
    this.selectedOptionIndex = (this.filteredOptions || this.flatGroupsOptions || []).findIndex(
      option => option.value === this.value,
    );
  }

  private calculateSelectedOption(): void {
    if (this.multiple) {
      // @ts-ignore
      this.selectedOptions = (<SelectOptionValue[]>this.value || [])
        .map(v => this.options.find(option => option.value === v))
        .filter(o => !!o)!;
    } else {
      this.selectedOption =
        this.findSelectedOption(
          this.hasGroups ? this.flatGroupsOptions : this.options || [],
          this.value,
        ) || this.emptyOption;
      this.selectedOptionLabel =
        this.selectedOption?.displayedSelectedLabel || this.selectedOption?.label || null;

      if (this.hasClearButton) {
        this.displayClearButton.set(!!this.value && !this.readOnly && !this.disabled);
      }
    }
  }

  private calculateOptions(): void {
    this.hasGroups ? this.setGroupsOptions() : this.setDefaultOptions();
  }

  private setGroupsOptions(): void {
    const opts = this.includeEmptyOption
      ? [this.emptyGroupedOption, ...this.groupedOptions]
      : this.groupedOptions;

    const filteredGroupsOptions = opts
      .map(({ id, title, options }) => {
        const filteredOptions =
          this.searchQuery !== null
            ? options
                .filter(item => this.matchSearchQuery(item))
                .map(option => ({ ...option, uuid: generateUuid() }))
            : options.map(option => ({ ...option, uuid: generateUuid() }));

        return {
          id,
          title,
          options: filteredOptions,
        };
      })
      .filter(group => group.options.length);

    if (this.groupedOptionsSort) {
      filteredGroupsOptions.sort(this.groupedOptionsSort);
    }

    this.flatGroupsOptions = (filteredGroupsOptions || this.groupedOptions)
      .map(group => group.options)
      .reduce((a, b) => a.concat(b), []);

    this.filteredGroupsOptions = filteredGroupsOptions;
    this.toggleVirtualScroll();
  }

  private toggleVirtualScroll() {
    const items = (this.hasGroups ? this.filteredGroupsOptions : this.filteredOptions) || [];
    this.virtualScrollThresholdExceeded = items.length > this.virtualScrollThreshold;
  }

  private setDefaultOptions(): void {
    const filteredOptions =
      this.searchQuery !== null
        ? this.formattedOptions.filter(item => this.matchSearchQuery(item))
        : this.formattedOptions;

    this.filteredOptions = this.limit ? filteredOptions.slice(0, this.limit) : filteredOptions;
    this.toggleVirtualScroll();
  }

  private matchSearchQuery(item: SelectOption<Val>): boolean {
    if (this.searchStrategy) {
      return this.searchStrategy(item, this.searchQuery);
    } else if (this.searchCaseSensitive) {
      return (
        item.label.includes(this.searchQuery!) ||
        this.concatAdditionalSearchKeys(item).includes(this.searchQuery!)
      );
    } else {
      return (
        includesWithTrim(item.label, this.searchQuery!, { trim: false }) ||
        includesWithTrim(this.concatAdditionalSearchKeys(item), this.searchQuery!, { trim: false })
      );
    }
  }

  private concatAdditionalSearchKeys(item: SelectOption<Val>): string {
    if (item && item.additionalSearchKeys && item.additionalSearchKeys.length) {
      return item.additionalSearchKeys.reduce((agg, key) => `${agg};${key}`);
    } else {
      return '';
    }
  }

  private adjustDropdownWidth(): void {
    this.dropdownWidth = this.dropdownContainer.nativeElement.offsetWidth;
  }

  private selectUp(): void {
    const startIndex = this.selectedOptionIndex;
    const collection = (this.hasGroups ? this.flatGroupsOptions : this.filteredOptions) || [];

    do {
      if (this.selectedOptionIndex === 0 || this.selectedOptionIndex > collection.length - 1) {
        this.selectedOptionIndex = collection.length - 1;
      } else {
        this.selectedOptionIndex--;
      }
    } while (
      this.selectedOptionIndex !== startIndex &&
      collection[this.selectedOptionIndex] &&
      collection[this.selectedOptionIndex].disabled
    );
  }

  private selectDown(): void {
    const startIndex = this.selectedOptionIndex;
    const collection = (this.hasGroups ? this.flatGroupsOptions : this.filteredOptions) || [];
    do {
      if (this.selectedOptionIndex >= collection.length - 1) {
        this.selectedOptionIndex = 0;
      } else {
        this.selectedOptionIndex++;
      }
    } while (
      this.selectedOptionIndex !== startIndex &&
      collection[this.selectedOptionIndex] &&
      collection[this.selectedOptionIndex].disabled
    );
  }

  private updateDropdownPostionAsync(): void {
    this.setTimeout(() => {
      if (this.selectDropdownOverlay.overlayRef) {
        this.selectDropdownOverlay.overlayRef.updatePosition();
      }
    });
  }

  private setTimeout(fn: () => any, time: number = 0): any {
    return setTimeout(() => {
      if (!this.destroyed) {
        fn();
      }
    }, time);
  }

  private findSelectedOption(options: SelectOption[], value: Val | null): SelectOption | null {
    for (const option of options) {
      if (option.value === value) {
        return option;
      }
      if (option.nestedOptions) {
        const foundOption = this.findSelectedOption(option.nestedOptions, value);
        if (foundOption) {
          return foundOption;
        }
      }
    }
    return null;
  }
}
